<template>
  <Navigation :title="pkg.name" />

  <div class="detail-hero bg-finger">
    <div class="hero-spacer"></div>
  </div>

  <div class="detail-body">
    <div class="detail-summary">
      <span class="pkg-name">{{ pkg.name }}</span>
      <span class="pkg-target">{{ pkg.grouping }}</span>
      <span class="pkg-consultation" v-if="pkg.is_consultation === '1'">
        {{ $t('freeConsult') }}
      </span>
      <span class="pkg-consultation" v-else>
        {{ $t('noConsult') }}
      </span>
      <span class="pkg-price">{{ formatPrice(pkg.price) }}</span>
    </div>

    <section class="detail-section">
      <p class="detail-section-title">{{ $t('features') }}</p>
      <ul class="feature-grid">
        <li v-for="fitur in pkg.content" :key="fitur" class="feature-item">
          <i class="bi bi-check-circle-fill feature-icon"></i>
          <span>{{ fitur }}</span>
        </li>
      </ul>
    </section>

    <!-- Mandatory consultation (included free) -->
    <section class="detail-section" v-if="pkg.is_consultation === '1' && !selected_counselor.name">
      <div class="card-priadi consult-card">
        <div class="consult-card-header">
          <i class="bi bi-check-circle-fill consult-card-icon"></i>
          <span class="consult-card-label">{{ $t('freeConsult') }}</span>
        </div>
        <button class="btn-priadi btn-priadi-block consult-card-btn" @click="openCounselorModal">
          {{ $t('selectCounselor') }}
        </button>
      </div>
    </section>

    <!-- Optional consultation (toggle to add) -->
    <section class="detail-section" v-if="pkg.is_consultation === '0' && !selected_counselor.name">
      <div class="card-priadi consult-card">
        <div class="consult-card-header">
          <span class="consult-card-label">{{ $t('includeConsultation') }}</span>
          <button
            class="consult-toggle-pill"
            :class="{ active: consultation_enabled }"
            @click="toggleConsultation"
            role="switch"
            :aria-checked="consultation_enabled">
            <span class="consult-toggle-thumb"></span>
          </button>
        </div>
      </div>
    </section>

    <!-- Selected counselor card -->
    <section class="detail-section" v-if="selected_counselor.name">
      <div class="card-priadi counselor-selected">
        <div class="counselor-selected-header">
          <i class="bi bi-check-circle-fill counselor-selected-icon"></i>
          <span class="counselor-selected-label">{{ $t('selectedCounselor') }}</span>
        </div>
        <div class="counselor-selected-info">
          <img
            :src="`/img/counselor/team-member-${selected_counselor.id}.jpg`"
            class="counselor-avatar"
            :alt="selected_counselor.name" />
          <div class="counselor-details">
            <p class="counselor-detail-row">
              <span class="counselor-detail-label">{{ $t('counselorName') }}</span>
              <span class="counselor-detail-value">{{ selected_counselor.name }}</span>
            </p>
            <p class="counselor-detail-row">
              <span class="counselor-detail-label">{{ $t('consultationType') }}</span>
              <span class="counselor-detail-value">{{ consultation_type.toUpperCase() }}</span>
            </p>
            <p class="counselor-detail-row">
              <span class="counselor-detail-label">{{ $t('counselorFee') }}</span>
              <span class="counselor-detail-value counselor-price">
                {{ pkg.is_consultation === '1' ? $t('free') : formatPrice(selected_counselor.price) }}
              </span>
            </p>
          </div>
        </div>
        <div class="counselor-actions">
          <button class="btn-priadi counselor-action-edit" @click="openCounselorModal">
            {{ $t('edit') }}
          </button>
          <button class="btn-priadi-outline counselor-action-remove" @click="cancel_counselor">
            {{ $t('cancel') }}
          </button>
        </div>
      </div>
    </section>

    <hr class="detail-divider" />

    <div class="card-priadi detail-price-summary">
      <div class="price-header">
        <i class="bi bi-receipt price-header-icon"></i>
        <span class="price-header-text">{{ $t('total') }}</span>
      </div>
      <div class="price-row">
        <span class="price-label">{{ $t('testPackage') }}</span>
        <span class="price-value">{{ formatPrice(pkg.price) }}</span>
      </div>
      <div class="price-row" v-if="selected_counselor.name">
        <span class="price-label">{{ $t('counselorFee') }}</span>
        <span class="price-value" :class="{ 'price-free': pkg.is_consultation === '1' }">
          {{ pkg.is_consultation === '1' ? $t('free') : formatPrice(selected_counselor.price) }}
        </span>
      </div>
      <div class="price-divider"></div>
      <div class="price-row price-row-total">
        <span class="price-label price-label-total">{{ $t('total') }}</span>
        <span class="price-value price-value-total">{{ formatPrice(totalPrice) }}</span>
      </div>
    </div>

    <button class="btn-priadi btn-priadi-block detail-continue-btn" @click="triggerPesan">
      {{ $t('continue') }} <i class="bi bi-arrow-right"></i>
    </button>
  </div>

  <BottomNav />

  <!-- Counselor selection modal -->
  <div
    class="modal fade"
    :class="{ show: showCounselorModal }"
    :style="{ display: showCounselorModal ? 'block' : 'none' }"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    aria-labelledby="counselorModalTitle">
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content detail-modal">
        <div class="detail-modal-header">
          <h2 class="detail-modal-title" id="counselorModalTitle">{{ $t('selectCounselor') }}</h2>
        </div>
        <div class="detail-modal-body">
          <div class="alert-priadi alert-priadi-info">
            <i class="bi bi-info-circle me-2"></i> {{ $t('contactAvailability') }}
          </div>

          <span class="consult-type-label">{{ $t('consultationType') }}</span>
          <div class="tabs-priadi">
            <button
              class="tab-priadi"
              :class="{ active: consultation_type === 'online' }"
              @click="consultation_type = 'online'">
              {{ $t('online') }}
            </button>
            <button
              class="tab-priadi"
              :class="{ active: consultation_type === 'offline' }"
              @click="consultation_type = 'offline'">
              {{ $t('offline') }}
            </button>
          </div>

          <div v-if="consultation_type === 'online'" class="counselor-list">
            <div
              v-for="(counselor, index) in counselors"
              :key="counselor.id"
              class="card-priadi card-priadi-interactive counselor-option"
              data-bs-dismiss="modal"
              @click="select_counselor(index)">
              <div class="counselor-option-inner">
                <img
                  :src="`/img/counselor/team-member-${counselor.id}.jpg`"
                  class="counselor-option-avatar"
                  :alt="counselor.name" />
                <div class="counselor-option-info">
                  <p class="counselor-option-name">{{ counselor.name }}</p>
                  <p class="counselor-option-price">
                    {{ pkg.is_consultation === '1' ? $t('free') : formatPrice(counselor.price) }}
                  </p>
                  <p class="counselor-option-mileage">{{ counselor.mileage }}+ {{ $t('satisfiedClient') }}</p>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="consultation_type === 'offline'" class="counselor-list">
            <div
              class="card-priadi card-priadi-interactive counselor-option"
              data-bs-dismiss="modal"
              @click="select_counselor(2)">
              <div class="counselor-option-inner">
                <img
                  :src="`/img/counselor/team-member-${counselors[2].id}.jpg`"
                  class="counselor-option-avatar"
                  :alt="counselors[2].name" />
                <div class="counselor-option-info">
                  <p class="counselor-option-name">{{ counselors[2].name }}</p>
                  <p class="counselor-option-price">
                    {{ pkg.is_consultation === '1' ? $t('free') : formatPrice(counselors[2].price) }}
                  </p>
                  <p class="counselor-option-mileage">{{ counselors[2].mileage }}+ {{ $t('satisfiedClient') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    class="modal-backdrop fade"
    :class="{ show: showCounselorModal }"
    :style="{ display: showCounselorModal ? 'block' : 'none' }">
  </div>
</template>

<script>
import Navigation from '../components/Navigation'
import BottomNav from '../components/BottomNav'
import Swal from 'sweetalert2'

export default {
  name: 'PackageDetail',
  components: { BottomNav, Navigation },
  data() {
    return {
      pkg: {},
      showCounselorModal: false,
      consultation_enabled: false,
      counselors: [
        {
          id: 2,
          name: 'Annas Baihaqi',
          price: '300000',
          mileage: 20000,
          activeDays: [
            { day: "Senin - Jum'at", hour: '10:00 - 16:45' },
            { day: 'Sabtu', hour: '10:00 - 14:45' },
          ],
        },
        {
          id: 3,
          name: 'Sheila Rumai Halida',
          price: '250000',
          mileage: 9000,
          activeDays: [
            { day: "Senin - Jum'at", hour: '10:00 - 16:45' },
            { day: 'Sabtu', hour: '10:00 - 14:45' },
          ],
        },
        {
          id: 4,
          name: 'Lucky Pramarta',
          price: '250000',
          mileage: 2500,
          activeDays: [
            { day: "Senin - Jum'at", hour: '10:00 - 16:45' },
            { day: 'Sabtu', hour: '10:00 - 14:45' },
          ],
        },
      ],
      selected_counselor: {
        id: '',
        name: '',
        price: '',
      },
      totalPrice: '',
      consultation_type: '',
    }
  },
  mounted() {
    document.body.classList.add('body-tinted')
    this.pkg = this.$store.getters.getPackage
    this.totalPrice = this.pkg.price
  },
  unmounted() {
    document.body.classList.remove('body-tinted')
  },
  methods: {
    formatPrice(price) {
      return 'Rp.' + Intl.NumberFormat('id').format(price) + ',-'
    },
    openCounselorModal() {
      this.showCounselorModal = true
    },
    closeCounselorModal() {
      this.showCounselorModal = false
    },
    toggleConsultation() {
      this.consultation_enabled = !this.consultation_enabled
      if (this.consultation_enabled) {
        this.openCounselorModal()
      } else {
        this.cancel_counselor()
      }
    },
    select_counselor(id) {
      this.closeCounselorModal()
      this.selected_counselor.id = this.counselors[id].id
      this.selected_counselor.name = this.counselors[id].name
      this.selected_counselor.price = this.counselors[id].price

      if (this.pkg.is_consultation === '0')
        this.totalPrice = parseInt(this.pkg.price) + parseInt(this.selected_counselor.price)
      else this.totalPrice = parseInt(this.pkg.price)
    },
    cancel_counselor() {
      this.consultation_enabled = false
      this.selected_counselor = {
        id: '',
        name: '',
        price: '',
      }
      this.totalPrice = parseInt(this.pkg.price)
    },
    triggerPesan() {
      this.$store.dispatch('setPackageData', {
        counselor_id: this.selected_counselor.id,
        counselor_name: this.selected_counselor.name,
        counselor_price: this.selected_counselor.price,
        total_price: this.totalPrice,
        consultation_type: this.consultation_type,
      })

      if (this.pkg.is_consultation === '1')
        if (!this.selected_counselor.name) Swal.fire('Error', this.$t('selectCounselorFirst'), 'warning')
        else this.$router.push('/add_transaction')
      else this.$router.push('/add_transaction')
    },
  },
}
</script>

<style scoped>
/* Hero */
.detail-hero {
  position: relative;
}

/* Body content */
.detail-body {
  padding: 0 var(--space-4) calc(var(--space-16) + var(--space-10));
}

/* Summary card — overlaps hero, uses pkg-card gradient from Package.vue */
.detail-summary {
  display: block;
  background-color: var(--color-surface);
  background-image: linear-gradient(
    to bottom,
    rgba(var(--color-primary-rgb), 0.08) 0%,
    rgba(var(--color-primary-rgb), 0.09) 40%,
    var(--color-surface) 100%
  );
  border: none;
  border-radius: var(--radius-lg);
  padding: var(--space-4);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  font-family: var(--font-family);
  margin-top: -4em;
  position: relative;
  z-index: 1;
  margin-bottom: var(--space-6);
}

/* Section */
.detail-section {
  margin-bottom: var(--space-6);
}

.detail-section-title {
  font-size: var(--text-md);
  font-weight: var(--font-semibold);
  color: var(--color-text-primary);
  text-align: center;
  margin-bottom: var(--space-4);
}

/* Feature grid — 2 columns */
.feature-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-2) var(--space-4);
  list-style: none;
  padding: 0;
  margin: 0;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  font-size: var(--text-base);
  color: var(--color-text-primary);
  line-height: var(--leading-normal);
}

.feature-icon {
  color: var(--color-primary);
  font-size: var(--text-sm);
  margin-top: 2px;
  flex-shrink: 0;
}

/* Consultation card */
.consult-card {
  padding: var(--space-4);
}

.consult-card-header {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin-bottom: var(--space-3);
}

.consult-card-header:last-child {
  margin-bottom: 0;
}

.consult-card-icon {
  color: var(--color-success);
  font-size: var(--text-md);
  flex-shrink: 0;
}

.consult-card-label {
  font-size: var(--text-base);
  font-weight: var(--font-medium);
  color: var(--color-text-primary);
  flex: 1;
}

.consult-card-btn {
  border-radius: var(--radius-full);
}

/* Pill toggle switch */
.consult-toggle-pill {
  position: relative;
  width: 48px;
  height: 28px;
  border-radius: var(--radius-full);
  background-color: var(--color-border);
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition: background-color var(--transition-base);
}

.consult-toggle-pill.active {
  background-color: var(--color-primary);
}

.consult-toggle-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 22px;
  height: 22px;
  border-radius: var(--radius-full);
  background-color: var(--color-surface);
  box-shadow: var(--shadow-sm);
  transition: transform var(--transition-base);
  pointer-events: none;
}

.consult-toggle-pill.active .consult-toggle-thumb {
  transform: translateX(20px);
}

.consult-toggle-pill:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

/* Selected counselor card */
.counselor-selected {
  padding: var(--space-4);
}

.counselor-selected-header {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-3);
}

.counselor-selected-icon {
  color: var(--color-success);
  font-size: var(--text-md);
}

.counselor-selected-label {
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  color: var(--color-text-primary);
}

.counselor-selected-info {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  margin-bottom: var(--space-4);
}

.counselor-avatar {
  width: 72px;
  height: 72px;
  border-radius: var(--radius-full);
  object-fit: cover;
  flex-shrink: 0;
}

.counselor-details {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.counselor-detail-row {
  margin: 0;
}

.counselor-detail-label {
  display: block;
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
}

.counselor-detail-value {
  display: block;
  font-size: var(--text-base);
  font-weight: var(--font-semibold);
  color: var(--color-text-primary);
}

.counselor-price {
  color: var(--color-primary);
}

.counselor-actions {
  display: flex;
  gap: var(--space-2);
}

.counselor-action-edit {
  flex: 1;
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-full);
  font-size: var(--text-sm);
}

.counselor-action-remove {
  flex: 1;
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-full);
  font-size: var(--text-sm);
}

/* Divider */
.detail-divider {
  border: none;
  border-top: 1px solid var(--color-border);
  margin: var(--space-4) 0;
}

/* Price summary */
.detail-price-summary {
  padding: var(--space-4);
  margin-bottom: var(--space-6);
}

.price-header {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-3);
}

.price-header-icon {
  color: var(--color-primary);
  font-size: var(--text-md);
}

.price-header-text {
  font-size: var(--text-base);
  font-weight: var(--font-semibold);
  color: var(--color-text-primary);
}

.price-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-2) 0;
}

.price-label {
  font-size: var(--text-base);
  color: var(--color-text-secondary);
}

.price-value {
  font-size: var(--text-base);
  font-weight: var(--font-medium);
  color: var(--color-text-primary);
}

.price-free {
  color: var(--color-success);
  font-weight: var(--font-semibold);
}

.price-divider {
  border: none;
  border-top: 1px solid var(--color-border);
  margin: var(--space-2) 0;
}

.price-row-total {
  padding-top: var(--space-3);
}

.price-label-total {
  font-size: var(--text-md);
  font-weight: var(--font-bold);
  color: var(--color-text-primary);
}

.price-value-total {
  font-size: var(--text-lg);
  font-weight: var(--font-bold);
  color: var(--color-primary);
}

/* Continue button */
.detail-continue-btn {
  border-radius: var(--radius-full);
  font-weight: var(--font-semibold);
}

/* Modal — pkg-modal pattern (scoped, mirrors Package.vue) */
.detail-modal {
  border-radius: var(--radius-lg);
  border: none;
  box-shadow: var(--shadow-lg);
  margin: var(--space-4);
  overflow: hidden;
}

.detail-modal-header {
  background: linear-gradient(
    160deg,
    rgba(var(--color-primary-rgb), 0.08) 0%,
    rgba(var(--color-primary-rgb), 0.02) 100%
  );
  padding: var(--space-6) var(--space-6) var(--space-4);
  text-align: center;
}

.detail-modal-title {
  font-size: var(--text-lg);
  font-weight: var(--font-bold);
  color: var(--color-text-primary);
  margin: 0;
}

.detail-modal-body {
  padding: var(--space-4) var(--space-6) var(--space-6);
}

.detail-modal-body .alert-priadi {
  margin-bottom: var(--space-4);
}

.detail-modal-body .tabs-priadi {
  margin-bottom: var(--space-4);
}

.counselor-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.counselor-option {
  cursor: pointer;
}

.counselor-option-inner {
  display: flex;
  align-items: center;
  gap: var(--space-4);
}

.counselor-option-avatar {
  width: 80px;
  height: 80px;
  border-radius: var(--radius-full);
  object-fit: cover;
  border: 2px solid var(--color-border);
  flex-shrink: 0;
}

.counselor-option-info {
  text-align: left;
}

.counselor-option-name {
  font-size: var(--text-base);
  font-weight: var(--font-semibold);
  color: var(--color-text-primary);
  margin-bottom: var(--space-1);
}

.counselor-option-price {
  font-size: var(--text-sm);
  font-weight: var(--font-bold);
  color: var(--color-primary);
  margin-bottom: var(--space-1);
}

.counselor-option-mileage {
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  margin-bottom: 0;
}

.consult-type-label {
  display: block;
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  color: var(--color-text-primary);
  margin-bottom: var(--space-2);
}
</style>
